/**
 * deviceProfile.js — 이 기기가 감당할 «양»을 한 곳에서 정한다.
 * 축은 픽셀(dpr·그림자)이 아니라 양(이웃 동 수·가시거리·타일 반경)이다 — 모바일이 죽는 건
 * 프레임이 아니라 GPU 메모리(`context-lost`)이고, dpr 을 낮춰도 씬에 올라간 지오메트리·
 * 텍스처는 한 바이트도 안 준다.
 * 설정은 계정 단위로 저장돼 기기 구분이 없다(`PUT /api/game/settings/{userId}`). 그래서 성능
 * 키는 기기 로컬(localStorage)에만 두고, 이 파일의 표가 그 기본값을 기기마다 다르게 준다.
 * ⚠ 한 번 계산하고 고정한다. 세션 중에 렌더 정책이 바뀌면 리마운트·깜빡임이 난다.
 */

import { stickyEnum } from '../engine/debugFlags';

// 소프트웨어 WebGL(하드웨어 가속 꺼진 크롬·헤드리스 SwiftShader) — 픽셀이 진짜 비싼 유일한 경우.
function detectSoftwareGL() {
  try {
    const c = document.createElement('canvas');
    const g = c.getContext('webgl2') || c.getContext('webgl');
    if (!g) return true;
    const ext = g.getExtension('WEBGL_debug_renderer_info');
    const r = ext ? g.getParameter(ext.UNMASKED_RENDERER_WEBGL) : g.getParameter(g.RENDERER);
    g.getExtension('WEBGL_lose_context')?.loseContext();
    return /swiftshader|software|llvmpipe/i.test(String(r));
  } catch { return false; }
}

/** 소프트웨어 GL 여부 — 한 번만 재서 들고 있는다(비용은 던져 버릴 WebGL 컨텍스트 하나).
 *  ⚠ 터치 판정보다 먼저 재야 한다 — 헤드리스 검증 캡처가 SwiftShader 위에서 후처리를 돌리면
 *  3fps 라 타임아웃한다. */
export const IS_SOFTWARE_GL = typeof window !== 'undefined' ? detectSoftwareGL() : false;

/** 고를 수 있는 프로파일 — 아래 두 표(`PROFILE_CAPS`·`PROFILE_LIMITS`)의 키와 같아야 한다. */
export const PROFILE_KEYS = ['pc-high', 'pc-low', 'mobile-standard', 'mobile-low'];

/** 이 기기가 실제로 무엇인가 — 자동 판정(강제와 무관하게 늘 계산한다). */
function detectProfile() {
  try {
    const coarse = window.matchMedia?.('(pointer: coarse)')?.matches ?? false;
    const narrow = window.innerWidth <= 768;
    if (coarse || narrow) {
      // deviceMemory 는 안드로이드 크롬만 준다(iOS 는 undefined). 모르면 표준으로 둔다 —
      // 모르는 걸 저사양으로 깎으면 아이폰이 영영 흐린 화면을 본다. 4GB 이하만 절전.
      const mem = navigator.deviceMemory;
      return (typeof mem === 'number' && mem <= 4) ? 'mobile-low' : 'mobile-standard';
    }
    if (IS_SOFTWARE_GL) return 'pc-low';
    return 'pc-high';
  } catch { return 'pc-high'; }
}

/** 이 기기가 자동 판정으로는 무엇인가. 강제 중이어도 이 값은 진짜를 가리킨다. */
export const DETECTED_PROFILE = typeof window !== 'undefined' ? detectProfile() : 'pc-high';

/**
 * 강제 프로파일 — PC 에서 폰 설정을 그대로 재현해 보는 문(Unreal 의 Preview Platform 과 같은 자리).
 * `?profile=mobile-standard` 로 켜고 `?profile=auto` 로 되돌린다(한 번 주면 굳는다).
 * ⚠ 표는 모듈 로드 시 한 번 읽히므로 **바꾸면 새로고침해야** 적용된다.
 */
export const FORCED_PROFILE = typeof window !== 'undefined'
  ? stickyEnum('profile', 'auto', ['auto', ...PROFILE_KEYS])
  : 'auto';

export const DEVICE_PROFILE = FORCED_PROFILE === 'auto' ? DETECTED_PROFILE : FORCED_PROFILE;
export const IS_PROFILE_FORCED = DEVICE_PROFILE !== DETECTED_PROFILE;
export const IS_MOBILE_PROFILE = DEVICE_PROFILE.startsWith('mobile');

// 강제 중인 걸 모르면 「왜 갑자기 흐리지·느리지」를 기기 탓으로 오진한다 — 그 자리를 콘솔에 남긴다.
if (IS_PROFILE_FORCED) {
  console.warn('[deviceProfile] 강제 ' + DEVICE_PROFILE + ' (이 기기는 실제로 ' + DETECTED_PROFILE + ')'
    + ' — 되돌리려면 ?profile=auto');
}

/**
 * 후처리 프레임버퍼로 HalfFloat(HDR)를 써도 되나 — false 면 EffectComposer 가
 * `frameBufferType={UnsignedByteType}` 으로 내려간다(GameCanvas.jsx).
 * ⚠ 모바일에서 HalfFloat 렌더타겟은 세션 «도중» 죽어 화면이 통째로 검어진다(GL 에러도
 *   컨텍스트 손실도 없이). 부팅 프로브로는 못 가른다 — 그때는 아직 멀쩡하다. 그래서 탐지가
 *   아니라 프로파일로 막는다: 무는 것은 블룸 계조, 얻는 것은 화면이 안 죽는 것.
 */
export const HALF_FLOAT_FB_OK = !IS_MOBILE_PROFILE;

/**
 * 프로파일별 「양」 상한. **처음 켠 기기의 기본값**이 곧 이 표다. 지렛대 순서는
 * multiDongCap(이웃 동 하나가 지형·건물·소품을 통째로 들고 온다) > viewDistance > 그림자다.
 * ⚠ dprScale 은 배수가 아니라 **절대 픽셀비**다(R3F `dpr` prop = setPixelRatio). 0.85 를
 *   넣으면 네이티브 dpr 3 인 폰이 화면을 0.28배로 그려 글자까지 뭉갠다.
 * formLight/macroVar/grade 는 셰이더 ALU 몇 줄이라 모바일도 켠다. AA·AO 는 렌더타겟이 느는
 * 축이라 끈다(AO 는 깊이+노멀 패스가 붙는다). pc-low 는 AA 만 살린다.
 *
 * ⚠ **pc-high 만 AO(N8AO)를 켠다.** 복셀 지형은 단차의 오목한 모서리가 전부 같은 밝기라
 *   AO 없이는 계단이 «판»으로 읽힌다 — 그 그늘을 기하로 넣으면 그리디 병합이 깨져 삼각형
 *   3.1배다(`terrainBlockMat.js` 머리말). 저VRAM 기기에서 풀스크린 깊이·노멀 타깃이 컨텍스트
 *   손실을 부르는 것이 이 값을 오래 꺼 두었던 이유인데, 지금은 `lowEndGfx` 게이트와
 *   `ssaoFailed` 자동 강등(`GameCanvas.jsx`)이 그 자리를 막는다. 모바일 셋은 그대로 끈다.
 */
export const PROFILE_CAPS = {
  'pc-high':         { multiDongCap: 5, dprScale: 1.0,  shadowsEnabled: true,  viewDistance: 400, aaEnabled: true,  ssaoEnabled: true,  formLight: 0.60, macroVar: 0.60, gradeEnabled: true },
  'pc-low':          { multiDongCap: 3, dprScale: 0.85, shadowsEnabled: false, viewDistance: 320, aaEnabled: true,  ssaoEnabled: false, formLight: 0.60, macroVar: 0.50, gradeEnabled: true },
  'mobile-standard': { multiDongCap: 2, dprScale: 1.25, shadowsEnabled: false, viewDistance: 260, aaEnabled: false, ssaoEnabled: false, formLight: 0.60, macroVar: 0.45, gradeEnabled: true },
  'mobile-low':      { multiDongCap: 1, dprScale: 0.6,  shadowsEnabled: false, viewDistance: 180, aaEnabled: false, ssaoEnabled: false, formLight: 0.45, macroVar: 0.30, gradeEnabled: true, ambientLifeEnabled: false },
};

/**
 * 기기 천장 — **설정이 아니다.** 저장되지 않고 매 세션 다시 계산된다. 유저 설정으로 흘리면
 * 계정 DB 로 새어 PC 에서 정한 천장이 폰으로 따라온다. 슬라이더 최대치도 이 표를 본다.
 *   viewCap 은 m · hfTileR/terrainLodR 은 { default, min, max } · hfTileRate 와
 *     hfBuildRate 는 **초당** 상한(null = 무제한)이다. 프레임당 상한으로는 속도를 못 정한다 —
 *     프레임당 1건도 60fps 면 초당 60건이라 안 걸린다.
 *   ⚠ postFx 는 후처리만 정한다. 그림자·AO 는 `lowEndGfx` 가 따로 막는다.
 *   ⚠ landmarkScale 0 금지 — `LANDMARK_CULL`(여의도·명동 1500m)은 600m 밖에서도 보여야 하는
 *     것들이다. pc-low 만 1.0 인 건 헤드리스 검증·홍보 촬영이 이 칸으로 잡혀서다.
 *   ⚠ hfTileR 이 타일 한 장(1024m)보다 작으면 발밑 타일까지 dispose 돼 바닥이 사라진다.
 *   ⚠ biomeFieldPx 는 픽셀이 아니라 메모리 축이다(2048 = 14.8MB 한 장).
 */
export // terrainLodR(LOD 링) 기본값은 그 기기의 viewCap 에 맞춘다 — 그 밖은 안개벽 뒤다.
//   폰에 600~800m 를 주면 안개벽 밖을 굽는 것이라 메모리·굽기만 내고 한 픽셀도 안 보인다.
//   mobile-low 는 0(끔) — 1m 반경 160m · 기본 가시 180m 라 링이 이을 띠가 20m 뿐이다.
//   ⚠ 슬라이더 상한(max)은 안 낮춘다 — 「내 기기 안에서 조절할 자유」는 남긴다.
// farColumnM — 원경 지형(FarTerrain) 기둥 한 변(m). 작을수록 격자가 촘촘해져 far.geo
// 타입드배열이 ~1/cs² 로 커진다. 저사양 폰은 크게 줘 geo 크기를 줄인다.
const PROFILE_LIMITS = {
  'pc-high':         { postFx: true,  dprCap: 1.5,  viewCap: 900, landmarkScale: 1.0,  shadowMap: 4096, hfTilePar: 6, hfTileRate: null, hfBuildInflight: 3, hfBuildPerFrame: 3, hfAssemblePerFrame: 2, hfBuildRate: null, hfTileR: { default: 2048, min: 128, max: 2048 }, terrainLodR: { default: 1024, min: 0, max: 2048 }, biomeFieldPx: 2048, farColumnM: 64 },
  'pc-low':          { postFx: false, dprCap: 1.0,  viewCap: 900, landmarkScale: 1.0,  shadowMap: 2048, hfTilePar: 6, hfTileRate: null, hfBuildInflight: 3, hfBuildPerFrame: 3, hfAssemblePerFrame: 2, hfBuildRate: null, hfTileR: { default: 2048, min: 128, max: 2048 }, terrainLodR: { default: 1024, min: 0, max: 2048 }, biomeFieldPx: 2048, farColumnM: 64 },
  'mobile-standard': { postFx: true,  dprCap: 1.25, viewCap: 400, landmarkScale: 0.60, shadowMap: 1024, hfTilePar: 2, hfTileRate: 3, hfBuildInflight: 2, hfBuildPerFrame: 1, hfAssemblePerFrame: 1, hfBuildRate: 3, hfTileR: { default: 1024, min: 128, max: 2048 }, terrainLodR: { default: 400, min: 0, max: 2048 }, biomeFieldPx: 1024, farColumnM: 256 },
  'mobile-low':      { postFx: false, dprCap: 1.0,  viewCap: 260, landmarkScale: 0.40, shadowMap: 1024, hfTilePar: 1, hfTileRate: 2, hfBuildInflight: 1, hfBuildPerFrame: 1, hfAssemblePerFrame: 1, hfBuildRate: 2, hfTileR: { default: 640,  min: 128, max: 2048 }, terrainLodR: { default: 260, min: 0, max: 2048 }, biomeFieldPx: 768, farColumnM: 256 },
};

/** 이 기기의 천장. 소프트웨어 GL 은 프로파일과 **무관하게** postFx 를 못 켠다. */
const _limits = PROFILE_LIMITS[DEVICE_PROFILE] || PROFILE_LIMITS['pc-high'];
export const DEVICE_LIMITS = { ..._limits, postFx: _limits.postFx && !IS_SOFTWARE_GL };

// hfTileR 은 손잡이가 아니라 «계산 결과»다 — 그리는 반경(블록지형 층)이 이미 정해 놓은
// 값이라, 따로 두면 링을 넓혀도 데이터 반경이 그대로여서 링 바깥이 조용히 빈다.
//   [식]   블록지형 반경 + 타일 한 장(1024m). 여유 한 장을 더하는 건 가장자리 한
//          점을 그리려면 그 점이 속한 타일이 통째로 있어야 해서다(히스테리시스 TILE_KEEP_R
//          = +384m 는 그 위에 붙는다).
//   [천장] 기기 프로필의 **기본값**(max 가 아니다) — 넘어서면 폰 메모리 크래시가 돌아온다.
//   [바닥] 타일 한 장. 그보다 작으면 발밑 타일조차 「반경 밖」으로 판정돼 dispose 된다(검은
//          화면). 프로필 기본값이 그보다 작은 저사양 기기는 그 기본값을 그대로 쓴다.
const HF_TILE_SIZE_M = 1024;   // heightfieldData 의 타일 = 128셀 × 8m. 코드 상수라 안 바뀐다.
export function deriveHfTileR(s = {}) {
  const lim = DEVICE_LIMITS.hfTileR;
  const ceil = lim?.default ?? 2048;
  const lodR = s.showTerrainBlocks ? Number(s.terrainLodR ?? 0) || 0 : 0;
  // 블록을 안 그리면(=하이트필드 메시가 땅이면) 유도할 바깥 층이 없으므로 기본값을 그대로 쓴다.
  const need = lodR > 0 ? lodR + HF_TILE_SIZE_M : ceil;
  const floor = Math.min(HF_TILE_SIZE_M, ceil);
  return Math.min(ceil, Math.max(floor, need));
}

/** 이 기기의 **기본값** — `DEFAULT_SETTINGS` 가 이걸 편다. 저장된 값이 있으면 언제나 저장이 이긴다. */
export const DEVICE_CAPS = PROFILE_CAPS[DEVICE_PROFILE] || PROFILE_CAPS['pc-high'];

// 진단용 — 로그에 「어느 프로파일로 돌았나」가 없으면 설정 탓인지 코드 탓인지 못 가른다.
if (typeof window !== 'undefined') {
  window.__deviceProfile = {
    profile: DEVICE_PROFILE, caps: DEVICE_CAPS, limits: DEVICE_LIMITS,
    detected: DETECTED_PROFILE, forced: IS_PROFILE_FORCED,
    mem: navigator.deviceMemory || null, nativeDpr: window.devicePixelRatio || null,
  };
}
